// StatementPreviewModal.tsx — Sprint 13. Read-only preview with
// owner / period / balance summary + an itemised fee breakdown
// (regular fees + a separate "Late Fees" panel).
//
// Sprint 146 (R14-4, ADR-141) — the line items now come from
// `statement.fees`, which the server already populates. This modal used to
// RE-DERIVE them from the page's whole fee list with
// `f.ownerId === ownerKey && f.period === statement.period`. That predicate is
// a second copy of a rule StatementService already owns, and the two copies
// disagreed: `POST /api/fees` never sets `period`, so an app-created fee has
// `period = null` while StatementService buckets it under `""` — and
// `null === ""` is false. The modal rendered a Previous/Current Balance with
// NO "Regular Fees" section at all, i.e. a bill for money owed with nothing
// itemised. Consuming the server's own list is the same "don't duplicate the
// rule" move as Sprint 141's `feeTypeRowKey`.
//
// Consequence worth naming: `StatementDTO.fees` is the *unpaid* fees rolled
// into the statement (StatementService filters `status != PAID`). So a settled
// fee no longer appears as a green line here. That is the honest shape — the
// itemisation now sums to exactly the current-period portion of Balance Due,
// and the settled total keeps its own "Fees Paid (this period)" row.
import React from 'react';
import { Button, Descriptions, Divider, Modal, Space, Tag, Typography } from 'antd';
import { DownloadOutlined, FileTextOutlined, MailOutlined } from '@ant-design/icons';
import { currency } from '../../utils/currency';

const { Text } = Typography;

export interface StatementPreviewModalProps {
  statement:  any | null;
  owner:      any | null;

  onClose:    () => void;
  onDownload: (statement: any) => void;
  onSend:     (statementId: string) => void;
}

export const StatementPreviewModal: React.FC<StatementPreviewModalProps> = ({
  statement, owner, onClose, onDownload, onSend,
}) => {
  if (!statement) {
    return <Modal open={false} onCancel={onClose} footer={null} />;
  }

  // The server's own list — see the header. The `Array.isArray` fallback covers
  // a malformed/cached payload; it is not a re-derivation.
  const stmtFees: any[] = Array.isArray(statement.fees) ? statement.fees : [];
  const regularFees = stmtFees.filter((f: any) => f.type !== 'LATE_FEE' && f.type !== 'late_fee');
  const lateFees    = stmtFees.filter((f: any) => f.type === 'LATE_FEE' || f.type === 'late_fee');

  const feeRow = (f: any) => {
    const isWaived = f.status === 'WAIVED' || f.status === 'waived';
    return (
      <div key={f.id} style={{
        display: 'flex', justifyContent: 'space-between',
        padding: '5px 0', fontSize: 13,
        borderBottom: '1px solid #f5f5f5',
        textDecoration: isWaived ? 'line-through' : 'none',
        color: isWaived ? '#aaa' : 'inherit',
      }}>
        <span>{f.description || f.type}</span>
        <span style={{
          fontWeight: 600,
          color: isWaived ? '#aaa'
            : f.status === 'PAID'    ? '#059669'
            : f.status === 'OVERDUE' ? '#dc2626'
            : '#d97706',
        }}>
          {currency(f.amount)}
          {isWaived && (
            <Tag color="purple" style={{ marginLeft: 6, fontSize: 10, padding: '0 5px' }}>
              Waived
            </Tag>
          )}
        </span>
      </div>
    );
  };

  return (
    <Modal
      open={!!statement}
      onCancel={onClose}
      title={
        <Space>
          <FileTextOutlined style={{ color: '#4f46e5' }} />
          Statement Preview — {owner?.name || '—'} | {statement.period}
        </Space>
      }
      width={600}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button
          key="pdf" type="primary" icon={<DownloadOutlined />}
          onClick={() => { onDownload(statement); onClose(); }}
          style={{ background: '#4f46e5', borderColor: '#4f46e5' }}
        >
          Download PDF
        </Button>,
        !statement.isSent && (
          <Button
            key="send" icon={<MailOutlined />}
            onClick={() => { onSend(statement.id); onClose(); }}
          >
            Send Email
          </Button>
        ),
      ].filter(Boolean)}
    >
      <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Owner" span={2}>{owner?.name || '—'}</Descriptions.Item>
        <Descriptions.Item label="Unit">Unit {owner?.unitNumber || '—'}</Descriptions.Item>
        <Descriptions.Item label="Period">{statement.period}</Descriptions.Item>
        <Descriptions.Item label="Previous Balance">
          {currency(statement.previousBalance)}
        </Descriptions.Item>
        {/* Sprint 134 (R12-8) — "Payments Made" overpromised: this is the sum of
            this period's fees that are settled (fees table), not money received in
            the period (condo.payments ledger). See ADR-129. */}
        <Descriptions.Item label="Fees Paid (this period)">
          {currency(statement.payments)}
        </Descriptions.Item>
        <Descriptions.Item label="Current Balance" span={2}>
          <Text strong style={{
            color: statement.currentBalance > 0 ? '#dc2626' : '#059669',
            fontSize: 16,
          }}>
            {currency(statement.currentBalance)}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Status" span={2}>
          {statement.isSent ? (
            <Tag style={{
              borderRadius: 20, fontSize: 11, padding: '0 10px',
              background: '#ecfdf5', color: '#059669', border: '1px solid #bbf7d0',
            }}>Sent</Tag>
          ) : (
            <Tag style={{
              borderRadius: 20, fontSize: 11, padding: '0 10px',
              background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a',
            }}>Draft</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      {regularFees.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{
            fontSize: 12, color: '#555',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Regular Fees
          </Text>
          <div style={{ marginTop: 6 }}>
            {regularFees.map(feeRow)}
          </div>
        </div>
      )}

      {/* R14-4: a statement with a balance and no lines used to render in
          silence. If the server ships no fees, say so rather than show nothing. */}
      {stmtFees.length === 0 && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          No fee details available for this period.
        </Text>
      )}

      {lateFees.length > 0 && (
        <div style={{
          marginTop: 8, padding: '10px 12px',
          background: '#fff7e6', borderRadius: 8,
          border: '1px solid #ffd591',
        }}>
          <Text strong style={{
            fontSize: 12, color: '#d97706',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Late Fees
          </Text>
          <div style={{ marginTop: 6 }}>{lateFees.map(feeRow)}</div>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontWeight: 700, color: '#d97706',
          }}>
            <span>Total Late Fees</span>
            <span>
              {currency(lateFees
                .filter((f: any) => f.status !== 'WAIVED')
                .reduce((s: number, f: any) => s + Number(f.amount), 0))}
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
};
